import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Table,
  Tooltip,
  Col,
  Typography,
  Modal,
  Form,
  Input,
  notification,
  Button,
} from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { Mail, Plus } from 'lucide-react';
import debounce from 'lodash/debounce';
import { Spinner, Button as CrcButton, Checkbox as CrcCheckbox } from '@crc-evans/ui-components';
import * as constants from '../utils/constants';
import * as utility from '../utils/utility';
import adminAxiosConfig from '../services/adminAxiosConfig';
import './AdminReportingEmailPage.css';
// import {
//  } from '../utils/axiosConfig';

const { Title, Text } = Typography;

/** Min px width for a report column header from its label (uppercase 11px + cell padding). */
const reportHeaderColWidth = (label, floor = 64) =>
  Math.max(floor, Math.ceil(label.length * 9) + 32);

const headerTitleWithTooltip = (text) => (
  <Tooltip title={text} placement="top">
    <span className="admin-reporting-email-col-header">{text}</span>
  </Tooltip>
);

const reportCheckColumn = (title, key, reportId) => {
  const width = reportHeaderColWidth(title);
  return {
    title: headerTitleWithTooltip(title),
    dataIndex: 'report_ids',
    key,
    align: 'center',
    width,
    render: (reportIds) => (reportIds?.includes(reportId) ? <CheckOutlined /> : ''),
  };
};

const ADMIN_REPORT_CHECK_COLUMNS = [
  reportCheckColumn('DSR', 'dsrReport', 1),
  reportCheckColumn('PPM', 'ppmReport', 2),
  reportCheckColumn('PPM Issues', 'ppmIssuesReport', 3),
  reportCheckColumn('Communications', 'communicationsReport', 4),
  reportCheckColumn('Build to Standard', 'builtToStandardReport', 5),
  reportCheckColumn('Service to Standard', 'servicetoStandardReport', 6),
  reportCheckColumn('QC', 'qcReport', 8),
  reportCheckColumn('Spare Parts Order', 'sparePartsOrder', 7),
  reportCheckColumn('Shipment', 'shipmentReport', 9),
  reportCheckColumn('Error report', 'errorReport', 13),
  reportCheckColumn('DPR', 'dprReport', 10),
  reportCheckColumn('Safety Stock', 'safetyStock', 12),
  reportCheckColumn('Space Log', 'spaceLogReport', 16),
  reportCheckColumn('Material Expiry', 'materialConsumptionReport', 14),
  reportCheckColumn('Consumables Order', 'consumablesOrder', 15),
];

const ADMIN_REPORTING_EMAIL_TABLE_SCROLL_X =
  56 +
  220 +
  ADMIN_REPORT_CHECK_COLUMNS.reduce((sum, col) => sum + col.width, 0) +
  96;

const AdminReportingEmailPage = () => {
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [emailData, setEmailData] = useState('');
  const [teamTitle, setTeamTitle] = useState('');
  const [editValue, setEditValue] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedTeamMemberData, setSelectedTeamMemberData] = useState('');
  const [emailForm] = Form.useForm();
  const [reportEmails, setReportEmails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchKeyExist, setSearchKeyExist] = useState('');
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiEndpoint = '/report-email-recipients';
  const assetManagementEndpoint = '/report-email-recipients';

  const reportMapping = {
    dsrReport: 1,
    ppmReport: 2,
    ppmIssuesReport: 3,
    communicationsReport: 4,
    builtToStandardReport: 5,
    servicetoStandardReport: 6,
    sparePartsOrder: 7,
    qcReport: 8,
    shipmentReport: 9,
    errorReport: Number(constants.REPORT_EMAIL_ERROR_ID),
    dprReport: 10,
    safetyStock: 12,
    materialConsumptionReport: 14,
    consumablesOrder: 15,
    spaceLogReport: Number(constants.REPORT_EMAIL_SPACE_LOG_ID),
  };

  const reverseReportMapping = {
    1: 'dsrReport',
    2: 'ppmReport',
    3: 'ppmIssuesReport',
    4: 'communicationsReport',
    5: 'builtToStandardReport',
    6: 'servicetoStandardReport',
    7: 'sparePartsOrder',
    8: 'qcReport',
    9: 'shipmentReport',
    13: 'errorReport',
    10: 'dprReport',
    12: 'safetyStock',
    14: 'materialConsumptionReport',
    15: 'consumablesOrder',
    16: 'spaceLogReport',
  };

  const reportEmailAccessOptions = [
    { value: 'dsrReport', label: 'DSR' },
    { value: 'ppmReport', label: 'PPM' },
    { value: 'ppmIssuesReport', label: 'PPM issues' },
    { value: 'communicationsReport', label: 'Communications' },
    { value: 'builtToStandardReport', label: 'Built to standard' },
    { value: 'servicetoStandardReport', label: 'Service to standard' },
    { value: 'qcReport', label: 'QC' },
    { value: 'sparePartsOrder', label: 'Spare Parts Order' },
    { value: 'shipmentReport', label: 'Shipment' },
    { value: 'errorReport', label: 'Error report (app / integration failures)' },
    { value: 'dprReport', label: 'DPR' },
    { value: 'safetyStock', label: 'Safety Stock' },
    { value: 'materialConsumptionReport', label: 'Material Expiry' },
    { value: 'consumablesOrder', label: 'Consumables Order' },
    { value: 'spaceLogReport', label: 'Space Log' },
  ];

  const selectedReportEmailAccess = Form.useWatch('reportEmailAccess', emailForm) || [];
  const allReportAccessValues = reportEmailAccessOptions.map((option) => option.value);
  const isAllReportAccessSelected =
    allReportAccessValues.length > 0 &&
    allReportAccessValues.every((value) => selectedReportEmailAccess.includes(value));

  const handleReportAccessToggle = useCallback((reportKey, nextChecked) => {
    const current = emailForm.getFieldValue('reportEmailAccess') || [];
    const exists = current.includes(reportKey);
    let next = current;

    if (nextChecked && !exists) {
      next = [...current, reportKey];
    } else if (!nextChecked && exists) {
      next = current.filter((key) => key !== reportKey);
    }

    emailForm.setFieldsValue({ reportEmailAccess: next });
    emailForm.validateFields(['reportEmailAccess']).catch(() => {});
  }, [emailForm]);

  const handleSelectAllReportAccess = useCallback(
    (nextChecked) => {
      const next = nextChecked ? reportEmailAccessOptions.map((option) => option.value) : [];
      emailForm.setFieldsValue({ reportEmailAccess: next });
      emailForm.validateFields(['reportEmailAccess']).catch(() => {});
    },
    [emailForm]
  );

  const columns = [
    {
      dataIndex: 'serialNo',
      key: 'serialNo',
      align: 'center',
      width: 56,
      render: (text, record, index) => index + 1,
    },
    {
      title: headerTitleWithTooltip('Email'),
      dataIndex: 'email',
      key: 'email',
      align: 'left',
      width: 220,
      ellipsis: true,
    },
    ...ADMIN_REPORT_CHECK_COLUMNS,
    {
      title: '',
      key: 'action',
      dataIndex: 'action',
      fixed: 'right',
      width: 96,
      render: (text, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
          <Tooltip title="Update">
            <EditOutlined
              style={{ fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Member">
            <DeleteOutlined
              style={{ fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showDeleteModal(record)}
            />
          </Tooltip>
        </div>
      ),
      align: 'center',
    },
  ];

  const fetchReportList = async () => {
    try {
      setIsLoading(true);
      const response = await adminAxiosConfig.get(apiEndpoint, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchKeyExist,
        },
      });
      if (response.data.message === 'success') {
        setReportEmails(response.data.data.data);
        setFilteredEmails(response.data.data.data);
        setTotal(response.data.data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching report list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchValue) => {
      try {
        setIsLoading(true);
        const response = await adminAxiosConfig.get(apiEndpoint, {
          params: {
            page: currentPage,
            limit: pageSize,
            search: searchValue,
          },
        });
        if (response.data.message === 'success') {
          setReportEmails(response.data.data.data);
          setFilteredEmails(response.data.data.data);
          setTotal(response.data.data.pagination.total);
        }
      } catch (error) {
        console.error('Error searching emails:', error);
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [currentPage, pageSize]
  );

  const onReportSearch = (e) => {
    const searchValue = e.target.value;
    setSearchKeyExist(searchValue);
    debouncedSearch(searchValue);
  };

  const renderColumnTitle = (title, dataIndex) => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{title}</span>
      </div>
      <div className="header-filter-input">
        <Input
          placeholder="Search"
          size="small"
          value={searchKeyExist}
          onChange={onReportSearch}
          onClick={(e) => e.stopPropagation()}
          prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
          style={{ width: '180px' }}
        />
      </div>
    </div>
  );

  useEffect(() => {
    fetchReportList();
  }, [currentPage, pageSize]);

  const showModal = () => {
    setTeamTitle('Add Email');
    setEmailModalVisible(true);
  };

  const showDeleteModal = (record) => {
    setIsDeleteModalVisible(true);
    setSelectedTeamMemberData(record);
  };

  const showEditModal = (record) => {
    const emailDt = {
      email: record.email,
      reportEmailAccess: record.report_ids.map((id) => reverseReportMapping[id]).filter(Boolean),
    };
    emailForm.setFieldsValue(emailDt);
    setEmailData(record.email);
    setEditValue(true);
    setTeamTitle('Update Email');
    setEmailModalVisible(true);
  };

  const handleCancel = () => {
    emailForm.resetFields();
    setEmailModalVisible(false);
    setEditValue(false);
  };

  const handleOk = async () => {
    emailForm.validateFields().then(async (data) => {
      if (data.reportEmailAccess.length && data.email) {
        try {
          setIsSubmitting(true);
          const email = String(data.email).trim();
          const reportIds = data.reportEmailAccess.map((report) => reportMapping[report]);

          // Add = POST once; Update = PUT once. Never retry on failure.
          const response = !editValue
            ? await adminAxiosConfig.post(assetManagementEndpoint, {
              email,
              report_ids: reportIds,
            })
            : await adminAxiosConfig.put(assetManagementEndpoint, {
              email,
              report_ids: reportIds,
            });

          if (response.data.message === 'success') {
            await fetchReportList();
            emailForm.resetFields();
            setEmailModalVisible(false);
            setEditValue(false);
            notification.success({
              message: !editValue ? 'Email added successfully' : 'Email updated successfully',
            });
          }
        } catch (error) {
          console.error('Error with email operation:', error);
          const apiMsg =
            (typeof error === 'object' && error?.error) ||
            error?.message ||
            '';
          if (/already has recipients/i.test(String(apiMsg))) {
            notification.warning({
              message: 'This email is already registered. Open it from the list to update report access.',
            });
          } else {
            notification.error({
              message: !editValue ? 'Error adding email' : 'Error updating email',
            });
          }
        } finally {
          setIsSubmitting(false);
        }
      } else {
        notification.warning({
          message: 'Please fill all the required fields!',
        });
      }
    });
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedTeamMemberData('');
  };

  const handleDeleteOk = async () => {
    if (selectedTeamMemberData.email) {
      try {
        setIsSubmitting(true);
        const response = await adminAxiosConfig.delete(
          `${assetManagementEndpoint}/${selectedTeamMemberData.email}`
        );
        if (response.data.message === 'success') {
          await fetchReportList();
          setIsDeleteModalVisible(false);
          setSelectedTeamMemberData('');
          notification.success({
            message: 'Email deleted successfully',
          });
        }
      } catch (error) {
        console.error('Error deleting email:', error);
        notification.error({
          message: 'Error deleting email',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="heat-profile-content-wrapper">
      <div className="heat-profile-card">
        <div className="heat-profile-list-header">
          <div className="heat-profile-header-left">
            <div className="heat-profile-icon-wrap" style={{ backgroundColor: '#e0f2fe' }}>
              <Mail size={24} color="#0369a1" />
            </div>
            <div className="heat-profile-header-content">
              <div className="heat-profile-title-row">
                <h3 className="heat-profile-list-title">Reporting Email Recipients</h3>
              </div>
              <p className="heat-profile-list-description">
                Manage email addresses that receive automated reports.
              </p>
            </div>
          </div>
          <div className="heat-profile-header-actions calibration-modern-actions">
            <CrcButton
              label="Add Email"
              variant="primary"
              icon={Plus}
              onClick={showModal}
              size="small"
            />
          </div>
        </div>

        <div className="heat-profile-table-wrap admin-reporting-email-table-wrap" style={{ marginTop: '0px' }}>
          <Table
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: (page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize);
              },
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              className: 'heat-profile-pagination',
            }}
            columns={columns}
            dataSource={filteredEmails}
            className="heat-profile-table admin-reporting-email-table"
            scroll={{ x: ADMIN_REPORTING_EMAIL_TABLE_SCROLL_X }}
            loading={{
              spinning: isLoading,
              indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
            }}
            rowKey="email"
          />
        </div>
      </div>

      <Modal
        width={600}
        title={
          <span style={{ fontWeight: 'bold', color: '#000', fontSize: '18px' }}>
            {teamTitle}
          </span>
        }
        centered
        className="heatprofile-modal"
        open={emailModalVisible}
        onCancel={handleCancel}
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleOk}
            loading={isSubmitting}
          >
            {!editValue ? 'Add Email' : 'Update Email'}
          </Button>,
        ]}
      >
        <Form
          name="basic"
          layout="vertical"
          autoComplete="off"
          form={emailForm}
        >
          <Row gutter={24}>
            <Col lg={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                    message: 'Please enter an Email.',
                  },
                  {
                    pattern: constants.EMAIL_PATTERN,
                    message: 'Provide a valid Email.',
                  },
                ]}
                normalize={(value) => utility.trimSpacesInValues(value)}
              >
                <Input
                  size="large"
                  placeholder="Enter Email"
                  disabled={editValue}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            className="mb-0"
            label="Report Email Access"
            name="reportEmailAccess"
            rules={[
              {
                required: true,
                message: 'At least one Report Email Access is required.',
              },
            ]}
          >
            <div className="w-100">
              <div style={{ marginBottom: 16, paddingBottom: 4 }}>
                <CrcCheckbox
                  label="Select ALL"
                  checked={isAllReportAccessSelected}
                  onChange={handleSelectAllReportAccess}
                  disabled={isSubmitting}
                  borderColor="#00C9A7"
                  tickColor="#00C9A7"
                />
              </div>
              <Row>
                {reportEmailAccessOptions.map((option) => (
                  <Col
                    key={option.value}
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    className="mb-1"
                  >
                    <CrcCheckbox
                      label={option.label}
                      checked={selectedReportEmailAccess.includes(option.value)}
                      onChange={(nextChecked) => handleReportAccessToggle(option.value, nextChecked)}
                      disabled={isSubmitting}
                      borderColor="#00C9A7"
                      tickColor="#00C9A7"
                    />
                  </Col>
                ))}
              </Row>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        width={500}
        className="heatprofile-modal"
        title={
          <span style={{ fontWeight: 'bold', color: '#000', fontSize: '18px' }}>
            Delete Team Member
          </span>
        }
        centered
        open={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        footer={[
          <Button key="cancel" onClick={handleDeleteCancel} disabled={isSubmitting}>
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            danger
            onClick={handleDeleteOk}
            loading={isSubmitting}
          >
            Confirm Delete
          </Button>,
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text style={{ fontSize: '16px', color: '#374151' }}>
            Are you sure you want to delete this team member?
          </Text>
          <div
            style={{
              marginTop: '12px',
              fontWeight: 'bold',
              color: '#000',
              fontSize: '18px',
            }}
          >
            {selectedTeamMemberData && selectedTeamMemberData.email}
          </div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>
            This action cannot be undone and will remove the access permanently.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AdminReportingEmailPage;
